import React from 'react';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {getPlanFeatures} from '@/views/content-studio/pricing/utils';
import styles from './Features.module.scss';

export type FeaturesProps = {
  bundle: ContentLabPlanBundle;
  hasTitle?: boolean;
  seatsCount?: number;
  videosCount?: number;
  classname?: string;
};

type FeatureProps = {
  text: string;
  icon?: React.ReactNode;
};

function Feature({text, icon}: FeatureProps) {
  return (
    <div className={styles.feature}>
      <Icon name="Actions-Accept" size="small" appearance="primary" />
      <Body size="md">{text}</Body>
      {icon}
    </div>
  );
}

function VideosQuestionMark() {
  return (
    <Tooltip
      title={translate(
        'Video = 30 seconds of content. For example, a 01:30 clip counts as 3 videos.'
      )}
      type="dark"
      placement="top"
      arrow
      PopperProps={{style: {width: '170px', whiteSpace: 'normal'}}}
    >
      <div>
        <Icon
          name="Navigation-Question-Line"
          size="small"
          appearance="primary"
          color={designSystemToken('semantic.bg.alpha-secondary')}
          className={styles.questionMarkIcon}
        />
      </div>
    </Tooltip>
  );
}

export function Features({
  bundle,
  hasTitle = true,
  seatsCount,
  videosCount,
  classname,
}: FeaturesProps) {
  const planFeatures = getPlanFeatures(bundle);

  return (
    <div className={`${styles.features} ${classname ?? ''}`}>
      {hasTitle && <Headline size="xxs">{translate(`Features:`)}</Headline>}
      {videosCount && (
        <Feature
          text={translate('{count} videos per month', {count: videosCount})}
          icon={<VideosQuestionMark />}
        />
      )}
      {seatsCount && (
        <Feature text={translate('{count} seats', {count: seatsCount})} />
      )}
      {planFeatures.map((feature: string) => (
        <Feature key={feature} text={feature} />
      ))}
    </div>
  );
}

export default Features;
